/**
 * What the floating multitask window does (2026-08-02 directive; same-day amendment: sizing is ENTIRELY
 * customizable — every edge and corner resizes, plus maximize/restore and double-click-header maximize; lifted
 * out of src/components/FilePreview/FloatingPreviewWindow.tsx unchanged, PV5-8:C part 1, 2026-10-13). Drag by
 * the header, focus bumps z-order; geometry math is pure (windowGeometry.ts) and clamped so a window can never be
 * dragged beyond recovery. The windows draw what this answers.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  applyDrag,
  applyEdgeResize,
  clampWindowRect,
  clearReservedRight,
  maximizedRect,
  ResizeEdge,
  WindowRect,
} from '../../components/FilePreview/windowGeometry';
import {
  DOCKED_COMPANION_MIN_VIEWPORT_PX,
  companionReservedRightPx,
  useIsChatDockedCompanion,
} from '../../components/GlobalChatSystem/dockedCompanion';
import { resolveThreeDScene, ResolvedScene } from '../../components/FilePreview/resolve3d';

/** Grab strips on every edge + corner (entirely-customizable sizing). */
export const EDGE_PX = 7;
export const CORNER_PX = 16;

export const RESIZE_HANDLES: Array<{ edge: ResizeEdge; cursor: string; style: React.CSSProperties }> = [
  { edge: 'n', cursor: 'ns-resize', style: { top: 0, left: CORNER_PX, right: CORNER_PX, height: EDGE_PX } },
  { edge: 's', cursor: 'ns-resize', style: { bottom: 0, left: CORNER_PX, right: CORNER_PX, height: EDGE_PX } },
  { edge: 'e', cursor: 'ew-resize', style: { top: CORNER_PX, bottom: CORNER_PX, right: 0, width: EDGE_PX } },
  { edge: 'w', cursor: 'ew-resize', style: { top: CORNER_PX, bottom: CORNER_PX, left: 0, width: EDGE_PX } },
  { edge: 'ne', cursor: 'nesw-resize', style: { top: 0, right: 0, width: CORNER_PX, height: CORNER_PX } },
  { edge: 'nw', cursor: 'nwse-resize', style: { top: 0, left: 0, width: CORNER_PX, height: CORNER_PX } },
  { edge: 'se', cursor: 'nwse-resize', style: { bottom: 0, right: 0, width: CORNER_PX, height: CORNER_PX } },
  { edge: 'sw', cursor: 'nesw-resize', style: { bottom: 0, left: 0, width: CORNER_PX, height: CORNER_PX } },
];

export interface FloatingPreviewWindowView {
  isMaximized: boolean;
  bodyRef: React.MutableRefObject<HTMLDivElement | null>;
  /** Pointer-down on the header (drag) or on an edge's strip (resize from that edge). */
  beginGesture: (mode: 'drag' | 'resize', edge?: ResizeEdge) => (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  endGesture: () => void;
  toggleMaximize: () => void;
  /** "Open chat" was taken: slide out from under the rail the chat is about to hold. */
  stepAsideForChat: () => void;
  fullscreen: () => void;
  /** A press on a header tool must not also start a window drag. */
  stopDrag: (e: React.PointerEvent) => void;
}

export function useFloatingPreviewWindow({ rect, onFocus, onRect }: {
  rect: WindowRect;
  onFocus: () => void;
  onRect: (rect: WindowRect) => void;
}): FloatingPreviewWindowView {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // Live "the chat holds the right rail" bit — maximize sizes around it.
  const isChatCompanion = useIsChatDockedCompanion();
  const gesture = useRef<{
    mode: 'drag' | 'resize';
    edge: ResizeEdge;
    startX: number;
    startY: number;
    origin: WindowRect;
  } | null>(null);
  // Maximize/restore: remember where the window was so Restore puts it back.
  const restoreRef = useRef<WindowRect | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);

  const beginGesture = (mode: 'drag' | 'resize', edge: ResizeEdge = 'se') => (e: React.PointerEvent) => {
    e.preventDefault();
    onFocus();
    gesture.current = { mode, edge, startX: e.clientX, startY: e.clientY, origin: rect };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    // Any manual drag/resize leaves maximized mode (the window is the user's now).
    if (isMaximized && (dx !== 0 || dy !== 0)) setIsMaximized(false);
    const next = g.mode === 'drag'
      ? applyDrag(g.origin, dx, dy, window.innerWidth, window.innerHeight)
      : applyEdgeResize(g.origin, g.edge, dx, dy, window.innerWidth, window.innerHeight);
    onRect(next);
  };

  const endGesture = () => { gesture.current = null; };

  const toggleMaximize = () => {
    if (isMaximized && restoreRef.current) {
      onRect(clampWindowRect(restoreRef.current, window.innerWidth, window.innerHeight));
      setIsMaximized(false);
    } else {
      restoreRef.current = rect;
      // Maximize fills the screen the chat is NOT holding (2026-08-24), so
      // going big beside a docked pane gives the side-by-side view instead
      // of burying the conversation the user was watching.
      onRect(maximizedRect(
        window.innerWidth,
        window.innerHeight,
        companionReservedRightPx({ docked: isChatCompanion, viewportWidth: window.innerWidth }),
      ));
      setIsMaximized(true);
    }
  };

  /**
   * "Open chat" clicked: the pane is about to take the right rail, so a
   * window sitting there would be the very thing it covers. Slide out from
   * under it, keeping this window's size wherever it still fits. The rail's
   * footprint is asked for as docked (not the live bit) because the dock has
   * been REQUESTED and has not landed yet — and only above the split's
   * viewport floor, since a phone never docks at all.
   */
  const stepAsideForChat = () => {
    if (window.innerWidth < DOCKED_COMPANION_MIN_VIEWPORT_PX) return;
    setIsMaximized(false);
    onRect(clearReservedRight(
      rect,
      window.innerWidth,
      window.innerHeight,
      // The rail at the width the person keeps it (dockedWidth.ts), not the default.
      companionReservedRightPx({ docked: true, viewportWidth: window.innerWidth }),
    ));
  };

  // Keep the window recoverable across viewport resizes.
  useEffect(() => {
    const onWindowResize = () => onRect(clampWindowRect(rect, window.innerWidth, window.innerHeight));
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rect.x, rect.y, rect.width, rect.height]);

  return {
    isMaximized,
    bodyRef,
    beginGesture,
    onPointerMove,
    endGesture,
    toggleMaximize,
    stepAsideForChat,
    fullscreen: () => { bodyRef.current?.requestFullscreen?.().catch(() => undefined); },
    stopDrag: (e) => e.stopPropagation(),
  };
}

/** Why a 3D model could not be had: the resolver's own reasons, else "unavailable". */
export type ThreeDSceneFailure = 'not-found' | 'auth-required' | 'unavailable';

/** A 3D model resolved by its artifact id (prest0nResolve3dPreview), for the window and the /preview/3d page. */
export function useThreeDScene(artifactId: string): { scene: ResolvedScene | null; failure: ThreeDSceneFailure | null } {
  const [scene, setScene] = useState<ResolvedScene | null>(null);
  const [failure, setFailure] = useState<ThreeDSceneFailure | null>(null);
  useEffect(() => {
    let cancelled = false;
    resolveThreeDScene(artifactId)
      .then((s) => { if (!cancelled) setScene(s); })
      .catch((e: Error) => {
        if (cancelled) return;
        setFailure(e?.message === 'not-found' ? 'not-found' : e?.message === 'auth-required' ? 'auth-required' : 'unavailable');
      });
    return () => { cancelled = true; };
  }, [artifactId]);
  return { scene, failure };
}
